import { defineComponent as _defineComponent } from 'vue'
import * as zoom from "d3-zoom";
import { select, Selection } from "d3-selection";
import GraphStronglyConnectedComponent from "@/components/visual-navigator/graph/graph-strongly-connected-component.vue";
import ViewVertex from "@/components/visual-navigator/graph/view-vertex";
import ViewGraph from "@/components/visual-navigator/graph/view-graph";
import ViewEdge from "@/components/visual-navigator/graph/view-edge";
import { isObject } from "@stellarbeat/js-stellarbeat-shared/lib/typeguards";
import { computed, onMounted, PropType, ref, toRefs, watch } from "vue";
import { useTruncate } from "@/composables/useTruncate";


export default /*#__PURE__*/_defineComponent({
  __name: 'graph',
  props: {
  centerVertex: {
    type: Object as PropType<ViewVertex>,
    required: false,
  },
  selectedVertices: {
    type: Array as PropType<ViewVertex[]>,
    required: true,
  },
  optionShowFailingEdges: {
    type: Boolean,
    required: true,
  },
  optionHighlightTrustingNodes: {
    type: Boolean,
    required: true,
  },
  optionHighlightTrustedNodes: {
    type: Boolean,
    required: true,
  },
  optionShowRegularEdges: {
    type: Boolean,
    required: true,
  },
  optionTransitiveQuorumSetOnly: {
    type: Boolean,
    required: true,
  },
  fullScreen: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  viewGraph: {
    type: Object as PropType<ViewGraph>,
    required: true,
  },
},
  emits: ["vertex-selected"],
  setup(__props, { emit }) {

const props = __props;



const {
  centerVertex,
  fullScreen,
  isLoading,
  selectedVertices,
  viewGraph,
  optionHighlightTrustingNodes,
  optionHighlightTrustedNodes,
  optionShowFailingEdges,
} = toRefs(props);

const truncate = useTruncate();

let d3svg: Selection<Element, null, null, undefined>;
let d3Grid: Selection<Element, null, null, undefined>;
/* eslint-disable  @typescript-eslint/no-explicit-any */
let graphZoom: any;

watch(
  () => props.centerVertex,
  () => {
    centerCorrectVertex();
  }
);

watch(fullScreen, () => {
  centerCorrectVertex();
  transformAndZoom();
});

watch(isLoading, () => {
  centerCorrectVertex();
});

function vertexSelected(vertex: ViewVertex) {
  emit("vertex-selected", vertex);
}

function getVertexTransform(vertex: ViewVertex): string {
  return `translate(${vertex.x},${vertex.y})`;
}

function getVertexTextRectWidth(vertex: ViewVertex) {
  return (truncate(vertex.label, 10).length / 10) * 60;
}

function getVertexTextRectWidthPx(vertex: ViewVertex) {
  return getVertexTextRectWidth(vertex) + "px";
}

function getVertexTextRectX(vertex: ViewVertex) {
  return "-" + getVertexTextRectWidth(vertex) / 2 + "px";
}

function getVertexTextClass(vertex: ViewVertex) {
  return {
    active: !vertex.isFailing,
    failing: vertex.isFailing,
    selected: vertex.selected,
  };
}

function getVertexClassObject(vertex: ViewVertex) {
  return {
    active: !vertex.isFailing,
    selected: vertex.selected,
    failing: vertex.isFailing,
    target: highlightVertexAsIncoming(vertex) && !vertex.selected,
    source:
      highlightVertexAsOutgoing(vertex) &&
      !vertex.selected &&
      !highlightVertexAsIncoming(vertex),
    transitive: vertex.isPartOfTransitiveQuorumSet,
  };
}

function highlightVertexAsOutgoing(vertex: ViewVertex) {
  if (selectedVertices?.value.length <= 0) return false;
  let edges = selectedVertices?.value
    .map((selectedVertex) =>
      viewGraph?.value.viewEdges.get(vertex.key + ":" + selectedVertex.key)
    )
    .filter((edge) => edge !== undefined);
  let allEdgesAreFailing = edges.every((edge) => (edge as ViewEdge).isFailing);

  if (edges.length <= 0) return false;

  return (
    vertex.isTrustingSelectedVertex &&
    optionHighlightTrustingNodes?.value &&
    (!allEdgesAreFailing || optionShowFailingEdges?.value)
  );
}

function highlightVertexAsIncoming(vertex: ViewVertex) {
  if (selectedVertices?.value.length <= 0) return false;

  let edges = selectedVertices?.value
    .map((selectedVertex) =>
      viewGraph?.value.viewEdges.get(selectedVertex.key + ":" + vertex.key)
    )
    .filter((edge) => edge !== undefined);
  let allEdgesAreFailing = edges.every((edge) => (edge as ViewEdge).isFailing);

  if (edges.length <= 0) return false;

  return (
    vertex.isTrustedBySelectedVertex &&
    optionHighlightTrustedNodes?.value &&
    (!allEdgesAreFailing || optionShowFailingEdges?.value)
  );
}

const graphSvg = ref<SVGElement | null>(null);

function width() {
  return (graphSvg.value as SVGElement).clientWidth;
}

function height() {
  return (graphSvg.value as SVGElement).clientHeight;
}

const dimmerClass = computed(() => {
  return {
    dimmer: true,
    active: isLoading?.value,
  };
});

function centerCorrectVertex() {
  if (centerVertex?.value !== undefined) {
    const realVertexX = -centerVertex.value.x + width() / 2;
    const realVertexY = -centerVertex.value.y + height() / 2;

    let transform = zoom.zoomIdentity
      .translate(realVertexX, realVertexY)
      .scale(1);
    d3svg.call(graphZoom.transform, transform);
  }
}

const grid = ref<Element | null>(null);
onMounted(() => {
  d3Grid = select(grid.value as Element);
  d3svg = select(graphSvg.value as Element);
  graphZoom = zoom
    .zoom()
    .on("zoom", (event) => {
      d3Grid.attr("transform", event.transform);
    })
    .scaleExtent([1, 3])
    .translateExtent([
      [-width(), -height()],
      [width(), height()],
    ]);
  transformAndZoom();
});

function transformAndZoom() {
  let transform = zoom.zoomIdentity
    .translate(width() / 2, height() / 2)
    .scale(1);
  d3svg.call(graphZoom).call(graphZoom.transform, transform);
}

function getEdgeClassObject(edge: ViewEdge) {
  return {
    "strongly-connected": edge.isPartOfStronglyConnectedComponent,
    failing: edge.isFailing,
  };
}

function getEdgePath(edge: ViewEdge) {
  if (!isObject(edge.source))
    throw new Error("Edge source not transformed into object by D3");
  if (!isObject(edge.target))
    throw new Error("Edge target not transformed into object by D3");
  return (
    "M" +
    edge.source.x +
    " " +
    edge.source.y +
    " L" +
    edge.target.x +
    " " +
    edge.target.y
  );
}

return { __sfc: true,props, centerVertex, fullScreen, isLoading, selectedVertices, viewGraph, optionHighlightTrustingNodes, optionHighlightTrustedNodes, optionShowFailingEdges, emit, truncate, d3svg, d3Grid, graphZoom, vertexSelected, getVertexTransform, getVertexTextRectWidth, getVertexTextRectWidthPx, getVertexTextRectX, getVertexTextClass, getVertexClassObject, highlightVertexAsOutgoing, highlightVertexAsIncoming, graphSvg, width, height, dimmerClass, centerCorrectVertex, grid, transformAndZoom, getEdgeClassObject, getEdgePath, GraphStronglyConnectedComponent }
}

})